import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UUID } from '@4zen/api-client';
import { api } from '@/lib/api';

export const clientKeys = {
  all: ['clients'] as const,
  list: (filters: Record<string, unknown>) => ['clients', 'list', filters] as const,
  detail: (id: UUID) => ['clients', 'detail', id] as const,
  timeline: (id: UUID) => ['clients', 'timeline', id] as const,
  transfers: (status?: string) => ['transfers', status ?? 'all'] as const,
};

export function useClients(filters: Parameters<typeof api.clients.list>[0] = {}) {
  return useQuery({
    queryKey: clientKeys.list(filters as Record<string, unknown>),
    queryFn: () => api.clients.list(filters),
    placeholderData: (previous) => previous,
  });
}

export function useClient(id: UUID | undefined) {
  return useQuery({
    queryKey: clientKeys.detail(id!),
    queryFn: () => api.clients.get(id!),
    enabled: Boolean(id),
  });
}

export function useTimeline(id: UUID | undefined, channel?: string) {
  return useQuery({
    queryKey: [...clientKeys.timeline(id!), channel ?? 'all'],
    queryFn: () => api.clients.timeline(id!, { limit: 100, channel }),
    enabled: Boolean(id),
  });
}

export function useCreateClient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => api.clients.create(body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: clientKeys.all }),
  });
}

export function useUpdateClient(id: UUID) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => api.clients.update(id, body),
    onSuccess: (detail) => {
      queryClient.setQueryData(clientKeys.detail(id), detail);
      void queryClient.invalidateQueries({ queryKey: clientKeys.all });
    },
  });
}

export function useAddInteraction(id: UUID) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => api.clients.addInteraction(id, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: clientKeys.timeline(id) });
      void queryClient.invalidateQueries({ queryKey: clientKeys.detail(id) });
      void queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}

export function useTransferClient(id: UUID) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { to_user_id: UUID; reason?: string; keep_access?: boolean }) =>
      api.clients.transfer(id, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: clientKeys.detail(id) });
      void queryClient.invalidateQueries({ queryKey: ['transfers'] });
    },
  });
}

export function useShareClient(id: UUID) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userIds: UUID[]) => api.clients.share(id, { user_ids: userIds }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: clientKeys.detail(id) }),
  });
}

export function useVoidClient(id: UUID) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (reason: string) => api.clients.void(id, reason),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: clientKeys.all });
    },
  });
}

export function useTransfers(status?: string) {
  return useQuery({
    queryKey: clientKeys.transfers(status),
    queryFn: () => api.transfers.list(status),
  });
}

export function useReviewTransfer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, approve, note }: { id: UUID; approve: boolean; note?: string }) =>
      api.transfers.review(id, approve, note),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['transfers'] });
      void queryClient.invalidateQueries({ queryKey: clientKeys.all });
    },
  });
}
